import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { Container, Row, Col } from "react-grid-system";
import { useTranslation } from "react-i18next";
import CalendarHeader from "views/b2bView/components/calendarView/components/calendarHeader";
import { CalendarMode, CalendarViewType, DATE_FORMAT, EntityType, UICommand, CalendarSlotStatus, RoutePath, IAppUserProfile, AppUserGrant, AppUserRole, DsLayout } from 'shared/constants';
import { it, enUS, Locale } from 'date-fns/locale';
import { addDays, addMonths, addWeeks, subDays, subMonths, subWeeks } from 'date-fns';
import { Direction } from "shared/constants";
import FullCalendar from '@fullcalendar/react';
import { Calendar, CalendarOptions, EventApi, EventClickArg } from '@fullcalendar/core';
import timeGridWeekPlugin from '@fullcalendar/timegrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import "./calendarView/styles/calendar.css";
import { bookSlot, getCalendarEvents, postCloneReservation } from "net/b2b";
import moment from "moment";
import { ICalendarDialogForm, ICalendarDialogFormData, ICalendarEvent, ICloneReservation } from "dataModels/b2b";
import { requestPending, requestPristine } from "state/reducer/requestStatusSlice";
import { useDispatch, useSelector } from "react-redux";
import "./calendarView/styles/events.css";
import { v4 as uuid } from "uuid";
import CalendarEventContent from "views/b2bView/components/calendarView/components/calendarEventContent";
import DialogCalendar from "views/b2bView/components/calendarView/DialogCalendar";
import NotAvailableSlotsForm from "views/b2bView/components/calendarView/DialogCalendar/components/notAvailableSlotsForm";

import { hideNotification, showNotification } from "state/reducer/notificationSlice";
import { INotificationAction } from "dataModels/stateModels/notification";
import RangeCalendarForm from "views/b2bView/components/calendarView/DialogCalendar/components/rangeCalendarForm";
import CalendarButtons from "views/b2bView/components/calendarView/components/calendarButtons";
import SlotBookingForm from "views/b2bView/components/calendarView/DialogCalendar/components/slotBookingForm";
import useAppUser from "hooks/useAppUser";
import { AppUser } from "ooAuth2/appUser";
import { Button, NotificationBadge, Select, Textlink } from "@ds/ds-snam/react";
import { dsPreSelectHandler, dsSelectHandler } from "shared/utils/dsComponentUtils";
import { ISelectLabel } from "@ds/ds-snam/dist/models/select";
import { getContract } from "net/contracts";
import { IContract, IContractElement } from "dataModels/contract";
import ClientContractFilter from "views/dashboardView/components/clientContractFilter";
import { RootState } from "state/appStore";
import DsBadge from "components/DsBadge";
import { buildPopUp } from "components/PopUp";


const localeMapping: { [key: string]: Locale } = {
    it,
    en: enUS,
};
interface ICalendarViewProps {
    showInteractions: boolean,
    _contractId?:string|undefined,
    _businessId?:string|undefined,
    isCloning?:boolean|undefined
    isCloningIup?:string|undefined
}
const CalendarView: React.FC<ICalendarViewProps> = (props): JSX.Element => {

    const appUserProfile: IAppUserProfile | undefined = useAppUser()?.userProfile;
    const appUser: AppUser | undefined = useAppUser();
    const appUserRole: AppUserRole | undefined = appUser?.getRole();
    const layoutMode = useSelector((state:RootState)=>{
        return state.dsLayout.mode;
    })
    
    const {showInteractions,isCloning,isCloningIup} = props;
    const { t, i18n } = useTranslation(["b2b", "app"]);
    const [searchParams] = useSearchParams();
    
    const calendarViewType: CalendarViewType = searchParams.get("draft") === "true" ?  CalendarViewType.DRAFT : CalendarViewType.PUBLIC;
    //passare questo come prop TODO ##################################

    const [currentDate, setCurrentDate] = useState<Date>(new Date());
    const [currentMode, setCurrentMode] = useState<CalendarMode>(CalendarMode.DAY);


    const calendarRef = useRef<FullCalendar | null>(null);
    //Default calendar dynamic options
    const [calendarOptions, setCalendarOptions] = useState<CalendarOptions>({
        allDaySlot: false,
        plugins: [timeGridWeekPlugin, timeGridPlugin, dayGridPlugin, interactionPlugin],
        editable: false,
        nowIndicator: false,
        headerToolbar: {
            start: '',
            center: '',
            end: '',
        },
        weekends: true,
        slotMinTime: '00:00:00',
        slotMaxTime: '24:00:00',
        slotDuration: '01:00:00',
        slotLabelContent: (arg: { date: Date }) => arg.date.getHours().toString(),
        slotEventOverlap: false,
        //firstDay:1,//week starts from monday, default sunday
        //weekNumberCalculation : "ISO"
        eventClassNames: 'custom-event-class'

    });
    const [calendarEvents, setCalendarEvents] = useState<ICalendarEvent[]>([]);
    const [calendarCssClass, setCalendarCssClass] = useState<string>('');
    const [language, setLanguage] = useState<string>('');

    //Route params change
    const location = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [cloneData,setCloneData] = useState<ICloneReservation|undefined>(undefined);

    // Language changes
    useEffect(() => {
        //const newLang: string = LANGUAGES.get(i18n.language)!.id!;
        //console.log(newLang)
        //setLocale(localeMapping[newLang]);
        setLanguage(i18n.language);
    }, [i18n.language]);
    
const sendCloneReservation = (/*cloneData:ICloneReservation*/) => {
        if(cloneData!==undefined){
        dispatch(requestPending());
        postCloneReservation(cloneData)
            .then((response: any) => {
                buildPopUp(dispatch,response,t,t("booking.cloneSlot.2"),'OK-422',t("booking.cloneSlot.3"),t("booking.cloneSlot.4"))
                navigate(RoutePath.B2B_BOOKINGS_INFO);
            })
            .catch(error => {
                console.log(error);
            })
            .finally(() => {
                dispatch(requestPristine());
            })
        }
    }

    const calendarEventClick = (arg: EventClickArg) => {

        if(showInteractions){
            if(calendarViewType===CalendarViewType.PUBLIC){
                const calendarEvent: EventApi | null | undefined = calendarRef.current?.getApi().getEventById(arg.event._def.publicId);
                const event = (arg.event._def);
                if (event.extendedProps.slotStatus === CalendarSlotStatus.AVAILABLE) {
                    const objEvent = {
                        id: event.publicId,
                        title: event!.title,
                        start: calendarEvent!.start!.toString(),
                        end: calendarEvent!.end!.toString(),
                        classNames: calendarEvent!.classNames,
                        extendedProps: {
                            tooltip: event.extendedProps.tooltip,
                            services: event.extendedProps.services ? event.extendedProps.services : [],
                            baysAvailable: event.extendedProps.baysAvailable ? event.extendedProps.baysAvailable : [],
                            slotsAvailable: event.extendedProps.slotsAvailable,
                            slotStatus: event.extendedProps.slotStatus,
                            reservations: event.extendedProps.reservations
                        }
                    }
                    //console.log(objEvent)
                    setSelectedEvent(objEvent);

                    if(isCloning){ //Is Cloning
                        const date = new Date(calendarEvent!.start!.toString());
                        // Format date as "YYYY-MM-DDT00:00:00"
                        const yyyy = date.getFullYear();
                        const mm = String(date.getMonth() + 1).padStart(2, '0');
                        const dd = String(date.getDate()).padStart(2, '0');
                        const _newDate = `${yyyy}-${mm}-${dd}T00:00:00`;
                        // Format time as "HH:mm"
                        const hours = String(date.getHours()).padStart(2, '0');
                        const minutes = String(date.getMinutes()).padStart(2, '0');
                        const _newTime = `${hours}:${minutes}`;

                        let cloningObj:ICloneReservation ={
                            codeIup:isCloningIup!,
                            newDate:_newDate,
                            newTime:_newTime
                        }
                        setCloneData(cloningObj);
                        setCommand(UICommand.CALENDAR_MOVE_RESERVATION);
                    }else{ //Is Booking
                        setCommand(UICommand.CALENDAR_BOOK_SLOT);
                    }
                    

                }
            }
        }



    };

    //When currentDate changes, set new date to calendar too
    useEffect(() => {
        const calendarApi = calendarRef.current?.getApi();
        if (calendarApi) {
            calendarApi.gotoDate(currentDate);
        }

    }, [currentDate]);

    //When currentDate, currentMode or language change, load events
    //Event tooltip is translated from backend side
    useEffect(() => {
        //console.log(currentDate, currentMode, eventDay)
        loadEvents();
    }, [currentDate, currentMode, location, i18n.language]);

    function getParsedDate(currentDate: number, calendarMode: CalendarMode) {
        const date = new Date(currentDate * 1000); // Converti epoch a millisecondi
        let parsedDate;
    
        switch (calendarMode) {
            case CalendarMode.DAY:
                // Imposta l'orario a mezzogiorno
                date.setUTCHours(0, 0, 0, 0);
                parsedDate = Math.floor(date.getTime() / 1000);
                break;
            case CalendarMode.WEEK:
                const dayOfWeek = date.getUTCDay();
                const startOfWeek = new Date(date);
                startOfWeek.setUTCDate(date.getUTCDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
                startOfWeek.setUTCHours(0, 0, 0, 0); // Imposta mezzogiorno
                parsedDate = Math.floor(startOfWeek.getTime() / 1000);
                break;
            case CalendarMode.MONTH:
                const startOfMonth = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
                startOfMonth.setUTCHours(0, 0, 0, 0); // Imposta mezzogiorno
                parsedDate = Math.floor(startOfMonth.getTime() / 1000);
                break;
            default:
                throw new Error("Invalid calendarMode");
        }
    
        return parsedDate;
    }
    const loadEvents = () => {
        let queryString = getClientContractQueryString();
        //TODO: SE LA VIEW TYPE E' LA SETTIMANA DEVO PRENDERE IL PRIMO GIORNO DELLA SETTIMANA, NON LA CURRENT DATE, IDEM PER MESE
        dispatch(requestPending());
        //draft: 0, 1 per gli slot in draft del calendario provvisorio – 0 per gli slot del calendario pubblico 
        //startDate: 0, timestamp 
        //viewType: 0, secondo enumeratore sopra
        let forMove = (isCloning && isCloning===true) ? true : false; 
        getCalendarEvents(
            calendarViewType,
            //moment.utc(currentDate).unix(), //TODO: SE LA VIEW TYPE E' LA SETTIMANA DEVO PRENDERE IL PRIMO GIORNO DELLA SETTIMANA, NON LA CURRENT DATE, IDEM PER MESE
            getParsedDate(moment.utc(currentDate).unix(), currentMode),
            currentMode,
            forMove,
            isCloningIup,
            queryString
        )
            .then((response: ICalendarEvent[]) => {
                //console.log(response.data);
                //setCalendarEvents(JSON.parse(JSON.stringify(response.data)));
                setCalendarEvents(response);
            })
            .catch(error => {
                console.log(error);
            })
            .finally(() => {
                dispatch(requestPristine());
            })
    }

    const changeDate = (direction: Direction) => {

        let changeDateFunction: Function = (date: string | number | Date, amount: number) => Date;

        switch (currentMode) {
            case CalendarMode.DAY:
                changeDateFunction = direction === Direction.LEFT ? subDays : addDays;
                break;
            case CalendarMode.WEEK:
                changeDateFunction = direction === Direction.LEFT ? subWeeks : addWeeks;
                break;
            case CalendarMode.MONTH:
                changeDateFunction = direction === Direction.LEFT ? subMonths : addMonths;
                break;
            default:
                break;
        }
        if (changeDateFunction) {
            const newDate = changeDateFunction(currentDate, 1);
            setCurrentDate(newDate);
        }

    }

    const switchMode = (mode: CalendarMode) => {
        setCurrentMode(mode);
    }
    //When current calendar mode changes, set calendar specific calendar options for that view
    useEffect(() => {
        let options: CalendarOptions = { ...calendarOptions }
        switch (currentMode) {
            case CalendarMode.DAY:
                calendarRef?.current?.getApi().changeView('timeGridDay', currentDate)
                setCalendarCssClass('calendarDay');
                options.height = '60vh';
                break;

            case CalendarMode.WEEK:
                calendarRef?.current?.getApi().changeView('timeGridWeek', currentDate);
                setCalendarCssClass('calendarWeek');
                options.height = '60vh';
                options.selectable = true; // cells are clickable
                options.eventMaxStack = 2;
                options.select = () => {

                };
                //============================================================================
                //--- Important: on week view, this set the first day according to current date position in the.
                //--- If you want to start to default (0 sunday), comment this.
                //--- If you want to start from monday, set this to 1
                options.firstDay = 1;
                //============================================================================
                options.dayHeaderContent = (args: { date: Date }) => {
                    const options: Intl.DateTimeFormatOptions = { day: 'numeric', weekday: 'long' };
                    return new Intl.DateTimeFormat(language, options).format(args.date);
                };
                break;

            case CalendarMode.MONTH:
                calendarRef?.current?.getApi().changeView('dayGridMonth', currentDate);
                setCalendarCssClass('calendarMonth');
                options.height = '65vh';
                options.dayMaxEventRows = 2; // Max event rows for each day
                options.moreLinkClick = 'popover';
                options.dayHeaderContent = (args: { date: Date }) => {
                    const options: Intl.DateTimeFormatOptions = { day: undefined, weekday: 'long' };
                    return new Intl.DateTimeFormat(language, options).format(args.date);
                };
                break;
            default:
                break;
        }
        setCalendarOptions(options);
        //calendarRef?.current?.render();
    }, [currentMode, language]);


    const getLegendElement = (color: string, label: string) => {
        return (
            <div key={uuid()} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <NotificationBadge
                    notificationStatus={"custom"}
                    badgeVariant={"default"}
                    badgeAriaLabel={"Badge aria label"}
                    isSmall={true}
                    backgroundColor={color}
                    badgeText={label}
                />
                <span className="legendaLabel--contract">
                    {label}
                </span>
               {/*<div style={{ width: "15px", height: "15px", borderRadius: "50%", backgroundColor: color }}></div>
                {label}*/}
            </div>
        );
    }
    const getLegend = () => {
        //const mainContainer: Element = document.querySelector('.mainContentContainer')!;
        //const containerCs: CSSStyleDeclaration = getComputedStyle(mainContainer);
        return calendarViewType === CalendarViewType.DRAFT ?
            getLegendElement(/*containerCs.getPropertyValue('--unavailable-color')*/"var(--Grafici-light-viola-1, #5B1295)", t('calendar.legend.4'))//"Non disponibile"
            :
            [
                getLegendElement(/*containerCs.getPropertyValue('--busy-color')*/"var(--Grafici-light-blu-grigio-2, #5773A3)", t('calendar.legend.3')),//"Occupato"
                getLegendElement(/*containerCs.getPropertyValue('--unavailable-color')*/"var(--Grafici-light-viola-1, #5B1295)", t('calendar.legend.4')),//"Non disponibile"
                getLegendElement(/*containerCs.getPropertyValue('--closed-color')*/"#320216", t('calendar.legend.5'))//"Chiuso"
            ];
    }

    //ACTIONS
    const [command, setCommand] = useState<UICommand>()
    const [selectedEvent, setSelectedEvent] = useState<ICalendarEvent>();

    //FILTER
    const [contractId, setContractId] = useState<string | undefined>();
    const [businessId, setBusinessId] = useState<string | undefined>();
    const [selectedBusiness, setSelectedBusiness] = useState<ISelectLabel | undefined>(); //Need this because /contract-list response json has only businessName and not businessId, otherwhise I could use businessId
    const selectSelectedBusiness = (_selectedBusiness : ISelectLabel | undefined) => {
        setSelectedBusiness(_selectedBusiness)
    }
    const selectContractId = (_contractId : string | undefined) => {
        setContractId(_contractId)
    }
    const selectBusinessId = (_businessId : string | undefined) => {
        setBusinessId(_businessId);
    }


    const getClientContractQueryString = () => {
        let queryString="?businessId="+"&contractId="

        if(appUserProfile?.dashboardGrants.includes(AppUserGrant.CALENDAR_MENU) && calendarViewType!==CalendarViewType.DRAFT){ //se sono in draft la richiesta è sempre pulita
            if(!showInteractions){
                if(props._businessId && props._businessId!=="-1"){
                    if(props._contractId && props._contractId!=="-1"){
                        queryString="?businessId="+props._businessId+"&contractId="+props._contractId
                    }else{
                        queryString="?businessId="+props._businessId+"&contractId="
                    }
                }
            }else{
                if(businessId && businessId!=='-1'){
                    if(contractId && contractId!=='-1'){
                        queryString="?businessId="+businessId+"&contractId="+contractId
                    }else{
                        queryString="?businessId="+businessId+"&contractId="
                    }
                }
            }
    
        }

        return queryString;
    }

    useEffect(()=>{
        loadEvents();
    },[contractId,businessId,props._contractId,props._businessId])

    return (
        <div>
            <Row>
                <Col>
                    {showInteractions ?
                        <>
                            {!isCloning &&
                                <p className="pageTitle">
                                    {/*Calendario*/}
                                    {calendarViewType === CalendarViewType.PUBLIC ? t('calendar.title.0') : t('calendar.title.1')}
                                </p>
                            }
                        </>
                    :
                        <div style={{background:''}}>
                            <div style={{float:'left',width:'fit-content',fontWeight:'bold',fontFamily:'Lato, Lato-local, sans-serif',fontSize:'120%',color:layoutMode===DsLayout.DARK_MODE?"white":"black"}}>
                                {t('calendar.title.0')}
                            </div>

                            <div style={{float:'right',width:'fit-content'}}>
                            <Textlink
                                textlinkSelected={false}
                                isTopBarVariant={true}
                                textlinkText={t('calendar.title.2')}
                                textlinkTextSize="SMALL"
                                isDisabled={false}
                                icon=""
                                iconPosition="right"
                                textlinkAriaLabel="aria-Textlink"
                                isExternalLink={false}
                                isRouterLink={true}
                                isDownloadLink={false}
                                animationPlay={false}
                                textlinkStopAnimation={true}
                                onClickLink={(event:Event)=>{
                                    navigate(RoutePath.B2B_CALENDAR+"?draft=false")
                                }}
                                />
                            </div>
                        </div>
                    }
                </Col>
            </Row>
            
            {showInteractions && calendarViewType!==CalendarViewType.DRAFT && !isCloning && //se sono in draft non visualizzo il filtro
                <ClientContractFilter selectContractId={selectContractId} selectBusinessId={selectBusinessId} selectSelectedBusiness={selectSelectedBusiness} selectedBusiness={selectedBusiness} inCalendar={true}/>
            }
            <Row style={{marginRight:'-30px'}}>
                <Col>
                    <CalendarHeader
                        locale={localeMapping[language]}

                        currentMode={currentMode}
                        switchMode={switchMode}

                        currentDate={currentDate}
                        changeDate={changeDate}

                        showModeSelectors={showInteractions}
                    />

                </Col>
            </Row>
            <Row style={{marginRight:showInteractions?'-30px':'-15px',marginLeft:showInteractions&&!(currentMode===CalendarMode.MONTH)?'-20px':"-15px"}}>
                <Col>
                    {/** Calendar component */}
                    {calendarOptions && <div className={calendarCssClass}>
                        <FullCalendar
                            ref={calendarRef}
                            locale={localeMapping[language]}
                            events={calendarEvents}
                            eventClick={ (appUserProfile?.calendarGrants.includes(AppUserGrant.BOOK_SLOT) || isCloning&&appUserRole===AppUserRole.TL_MANAGER)?calendarEventClick:undefined}
                            /*eventMouseEnter={(arg: EventHoveringArg) => {
                                const eventObj:EventImpl = arg.event;
                                console.log(calendarRef.current?.getApi().getEventById(eventObj.id));
                                console.log(eventObj.id, eventObj.extendedProps["tooltip"]);
                            }
                            }*/
                            eventContent={(arg) => {
                                //console.log(arg.event._def, arg.event.extendedProps)
                                //console.log( calendarRef.current?.getApi().getEventById(arg.event._def.publicId) );
                                const event: EventApi | null | undefined = calendarRef.current?.getApi().getEventById(arg.event._def.publicId);
                                //console.log(event)//?.title, event?.start, event?.end, event?.extendedProps)
                                return event ? <CalendarEventContent
                                    event={event}
                                    mode={currentMode}
                                /> : <></>
                            }}
                            {...calendarOptions}
                        />
                    </div>}

                </Col>
            </Row>
            <Row>
                <Col>
                    <div style={{ display: "flex", alignItems: "center", gap: "16px", padding: "12px 0 12px",color:layoutMode===DsLayout.DARK_MODE?"white":"black" }}>
                        {getLegend()}
                    </div>
                </Col>
            </Row>

            {showInteractions && !isCloning &&
                <CalendarButtons calendarViewType={calendarViewType} setCommand={setCommand} /> 
            }

            

            {command &&
                <DialogCalendar
                    command={command}
                    selectedEvent={selectedEvent}
                    loadEvents={loadEvents}
                    onClose={(command?: UICommand) => {
                        setCommand(command ? command : undefined);
                    }}
                    isDraft={calendarViewType}
                    {...(command === UICommand.CALENDAR_MOVE_RESERVATION ? { externalLogic: ()=>sendCloneReservation() } : {})}
                />
            }


        </div>);
}
export default CalendarView;